import React from 'react';
import validator from 'validator';
import { isEmpty } from 'lodash';
import { Formik, Form, FormikProps } from 'formik';
import { withTranslation } from 'react-i18next';
import { ImageIcon } from '@saleshandy/design-system';
import 'react-datepicker/dist/react-datepicker.css';
import DatePicker from 'react-datepicker';
import { Calendar, ChevronDown } from '@saleshandy/icons';
import Input from '../../input';
import Button from '../../atoms/button';
import { FormField, InputValidationType, IProps } from './types';
import ProspectContactFieldComponent from '../../atoms/prospect-contact-field/prospect-contact-field';
import { ProspectContactInfoCategory } from '../../atoms/prospect-contact-field/enum';
import ProspectLabelField from '../../atoms/prospect-contact-field/prospect-label-field';
import InputLabel from '../../input/input-label';
import InputNumber from '../../input-number';
import { Images } from '../../../../app-constants';
import Select from '../../select';
import { Placement } from '../../overlay-taget/enums';
import Icon from '../../atoms/icon';
import {
  accessibleOnClick,
  executeOnRequestStatusWithPrevStatusCheck,
} from '../../../../utils';
import InputContainer from '../../input/input-container';
import { Variant } from '../../input/enums';
import TruncatedTextWithAutoTooltip from '../../../../components/truncated-text-with-auto-tooltip';

class CustomForm extends React.Component<IProps> {
  private formRef = React.createRef<FormikProps<{ [x: string]: any }>>();

  constructor(props: IProps) {
    super(props);
    this.generateInputFields = this.generateInputFields.bind(this);
    this.getNameKey = this.getNameKey.bind(this);
    this.getRadioButtonValue = this.getRadioButtonValue.bind(this);
  }

  componentDidUpdate(prevProps: Readonly<IProps>): void {
    const { saveContactAttributesRequestStatus, submitStatus } = this.props;
    executeOnRequestStatusWithPrevStatusCheck({
      status: saveContactAttributesRequestStatus,
      prevStatus: prevProps.saveContactAttributesRequestStatus,
      onSuccess: () => {
        this.formRef.current?.setSubmitting(false);
      },
      onFailed: () => {
        this.formRef.current?.setSubmitting(false);
      },
    });

    // Reset isSubmitting when submitStatus changes from true to false
    // Only do this when saveContactAttributesRequestStatus is not provided
    // (to avoid interfering with forms that use saveContactAttributesRequestStatus)
    if (
      saveContactAttributesRequestStatus === undefined &&
      prevProps.submitStatus === true &&
      submitStatus === false
    ) {
      this.formRef.current?.setSubmitting(false);
    }
  }

  generateValidationObject = () => {
    const { fields } = this.props;
    return fields
      .filter((field) => field.validator)
      .reduce(
        (obj, item) => Object.assign(obj, { [item.name]: item.validator }),
        {},
      );
  };

  isValidPhoneNumber = (phone: string) => {
    const phoneRegex = /^(?:\+?[0-9](?:[0-9().\-\s]{6,30})?|[0-9(][0-9().\-\s]{5,13}[0-9])$/;
    const digitLengthRegex = /^(?:\D*\d\D*){7,15}$/;

    if (phoneRegex.test(phone) && digitLengthRegex.test(phone)) {
      return true;
    }
    return false;
  };

  shouldSkipPhoneNumberValidation = (phoneEntry: {
    phonenumber?: string;
    isLocked?: boolean;
  }) => {
    if (!phoneEntry) {
      return true;
    }
    if (phoneEntry?.isLocked) {
      return true;
    }
    const value = String(phoneEntry?.phonenumber ?? '')
      ?.trim()
      ?.toLowerCase();
    return value === 'not publicly listed';
  };

  validateMaxCharLimit = (value, limit = 1000) =>
    value.toString().length <= limit;

  getNameKey = (str) => str.toString().toLowerCase().split(' ').join('-');

  truncateText = (text: string, maxLength: number): string => {
    if (text.length > maxLength) {
      // Truncate the text and add "..." at the end
      return `${text.substring(0, maxLength)}...`;
    }

    // Text length is within the limit, no need to truncate
    return text;
  };

  // Compare email arrays ignoring primary email changes
  hasEmailChangedExcludingPrimary = (
    currentEmails: any[],
    initialEmails: any[],
  ): boolean => {
    if (!Array.isArray(currentEmails) || !Array.isArray(initialEmails)) {
      return JSON.stringify(currentEmails) !== JSON.stringify(initialEmails);
    }

    const currentPrimary = currentEmails?.find((e) => e?.isPrimary);
    const initialPrimary = initialEmails?.find((e) => e?.isPrimary);

    // ENABLE SAVE if primary email TYPE changed
    if (currentPrimary?.emailType !== initialPrimary?.emailType) {
      return true;
    }

    // ignore primary email VALUE change
    const currentNonPrimary = currentEmails?.filter((e) => !e?.isPrimary);
    const initialNonPrimary = initialEmails?.filter((e) => !e?.isPrimary);

    // Check if non-primary emails count changed (non-primary email added/removed)
    if (currentNonPrimary?.length !== initialNonPrimary?.length) {
      return true;
    }

    // Compare non-primary emails (this will detect changes to non-primary email values)
    return (
      JSON.stringify(currentNonPrimary) !== JSON.stringify(initialNonPrimary)
    );
  };

  // Compare phone number arrays ignoring primary phone changes
  hasPhoneNumberChangedExcludingPrimary = (
    currentPhones: any[],
    initialPhones: any[],
  ): boolean => {
    if (!Array.isArray(currentPhones) || !Array.isArray(initialPhones)) {
      return JSON.stringify(currentPhones) !== JSON.stringify(initialPhones);
    }

    const currentPrimary = currentPhones?.find((p) => p?.isPrimary);
    const initialPrimary = initialPhones?.find((p) => p?.isPrimary);

    // ENABLE SAVE if primary phone TYPE changed
    if (currentPrimary?.phoneType !== initialPrimary?.phoneType) {
      return true;
    }

    const currentNonPrimary = currentPhones?.filter((p) => !p?.isPrimary);
    const initialNonPrimary = initialPhones?.filter((p) => !p?.isPrimary);

    // Check if non-primary phone numbers count changed (non-primary phone added/removed)
    if (currentNonPrimary?.length !== initialNonPrimary?.length) {
      return true;
    }

    // Compare non-primary phone numbers (this will detect changes to non-primary phone values)
    return (
      JSON.stringify(currentNonPrimary) !== JSON.stringify(initialNonPrimary)
    );
  };

  // Check if form is dirty excluding primary email and phone number changes
  isFormDirtyExcludingContactFields = (
    currentValues: any,
    initialValues: any,
  ): boolean => {
    const allKeys = Array.from(
      new Set([
        ...Object.keys(currentValues || {}),
        ...Object.keys(initialValues || {}),
      ]),
    );

    // Check if any non-excluded field has changed
    // eslint-disable-next-line no-restricted-syntax
    for (const key of allKeys) {
      const currentValue = currentValues?.[key];
      const initialValue = initialValues?.[key];

      // Handle undefined/null cases
      if (currentValue === undefined && initialValue === undefined) {
        // eslint-disable-next-line no-continue
        continue;
      }

      // Special handling for email field - ignore primary email changes
      if (key === 'email') {
        if (this.hasEmailChangedExcludingPrimary(currentValue, initialValue)) {
          return true;
        }
        // eslint-disable-next-line no-continue
        continue;
      }

      // Special handling for phonenumber field - ignore primary phone changes
      if (key === 'phonenumber') {
        if (initialValue?.length === 0 && currentValue?.length > 0) {
          return true;
        }
        if (
          this.hasPhoneNumberChangedExcludingPrimary(currentValue, initialValue)
        ) {
          return true;
        }
        // eslint-disable-next-line no-continue
        continue;
      }

      // Deep comparison for other fields
      const currentStr = JSON.stringify(currentValue ?? null);
      const initialStr = JSON.stringify(initialValue ?? null);

      if (currentStr !== initialStr) {
        return true;
      }
    }

    return false;
  };

  getRadioButtonValue = (value: string) => {
    if (value === 'true' || value === 'Yes') {
      return 'Yes';
    }
    if (value === 'false' || value === 'No') {
      return 'No';
    }
    return null;
  };

  generateInputFields(
    values,
    handleChange,
    errors,
    touched,
    handleBlur,
    setFieldValue,
    setFieldTouched,
    primaryEmailChangeInProspectRequest,
    updatePrimaryNumberRequest,
    deleteEmailFromProspectRequest,
    deletePhoneFromProspectRequest,
    selectedProspectId,
  ) {
    const { fields, formMode } = this.props;

    const { fieldsData, isAiIconShow = false } = this.props;

    const DatePickerInput = React.forwardRef<
      HTMLInputElement,
      React.HTMLProps<HTMLInputElement> & {
        onClear?: () => void;
      }
    >(({ value, onClick, onChange, onClear }, ref) => (
      <div className="bs-input date-picker-container">
        <span {...accessibleOnClick(onClick)}>
          <Calendar className="gray-txt-15 d-flex" />
        </span>
        <input
          placeholder="Select Date"
          onClick={onClick}
          className="input bs-input"
          style={{ textIndent: 10 }}
          onChange={onChange}
          value={value ? value.toString() : ''}
          ref={ref}
          readOnly // important to avoid manual typing issues
        />

        <div className="d-flex align-items-center">
          {onClear && value && (
            <Icon
              className="cal-icon mr-1 sh-close close-icon"
              identifier="close"
              onClick={(e) => {
                e.stopPropagation();
                onClear?.();
              }}
            />
          )}
        </div>
      </div>
    ));

    const inputFields = fields.map((field: FormField, index: number) => {
      if (field.component !== 'long-text') {
        if (typeof field.component === 'string' && field.name === 'email') {
          return (
            <ProspectContactFieldComponent
              key={this.getNameKey(field.name)}
              setFieldValue={setFieldValue}
              values={values.email}
              handleBlur={handleBlur}
              errors={errors}
              fieldName="email"
              touched={touched}
              setFieldTouched={setFieldTouched}
              formMode={formMode}
              primaryEmailChangeInProspectRequest={
                primaryEmailChangeInProspectRequest
              }
              updatePrimaryNumberRequest={updatePrimaryNumberRequest}
              deleteEmailFromProspectRequest={deleteEmailFromProspectRequest}
              deletePhoneFromProspectRequest={deletePhoneFromProspectRequest}
              selectedProspectId={selectedProspectId}
            />
          );
        }
        if (
          typeof field.component === 'string' &&
          field.name === 'phonenumber'
        ) {
          return (
            <ProspectContactFieldComponent
              key={this.getNameKey(field.name)}
              setFieldValue={setFieldValue}
              values={values.phonenumber}
              fieldName="phonenumber"
              handleBlur={handleBlur}
              errors={errors}
              touched={touched}
              setFieldTouched={setFieldTouched}
              formMode={formMode}
              primaryEmailChangeInProspectRequest={
                primaryEmailChangeInProspectRequest
              }
              updatePrimaryNumberRequest={updatePrimaryNumberRequest}
              deleteEmailFromProspectRequest={deleteEmailFromProspectRequest}
              deletePhoneFromProspectRequest={deletePhoneFromProspectRequest}
              selectedProspectId={selectedProspectId}
            />
          );
        }
        if (
          typeof field.component === 'string' &&
          (field.name === 'decisionrole' || field.name === 'isb2b')
        ) {
          const yesNoOptions = [
            { key: 'Yes', value: 'Yes' },
            { key: 'No', value: 'No' },
          ];
          const currentValue = this.getRadioButtonValue(values[field.name]);
          return (
            <React.Fragment key={this.getNameKey(field.name)}>
              <ProspectLabelField index={index} formMode={formMode} />
              <div
                className={`bs-input-container bs-select-container ${field.styleClassNames}`}
              >
                <label className="bs-input-label-prefix">
                  <TruncatedTextWithAutoTooltip
                    text={field?.displayName}
                    className="d-flex"
                  />
                </label>
                <Select
                  options={yesNoOptions}
                  selectedOptionKey={currentValue}
                  optionRenderer={(option) => <span>{option?.value}</span>}
                  selectedOptionRenderer={([option]) => (
                    <span>{option?.value}</span>
                  )}
                  onChange={([option]) => setFieldValue(field.name, option.key)}
                  placement={Placement.Auto}
                  className={`${field.styleClassNames} custom-field-select`}
                  placeholder={`Select ${field.displayName} value`}
                  showSearch
                  filterOption={(value, option) =>
                    option?.value?.includes(value)
                  }
                  popperConfig={{
                    modifiers: [
                      {
                        name: 'flip',
                        options: {
                          fallbackPlacements: ['bottom'],
                          allowedAutoPlacements: ['top', 'bottom'],
                        },
                      },
                    ],
                  }}
                />
                {errors[field.name] && touched[field.name] && (
                  <span className="form-input-error-caption red-txt-12">
                    {errors[field.name]}
                  </span>
                )}
              </div>
            </React.Fragment>
          );
        }
        if (typeof field.component === 'string') {
          const fieldMetadata = fieldsData?.find(
            (item) => item?.fieldMetadata?.fieldId === field.id,
          )?.fieldMetadata?.metadata;

          const dropdownOptions =
            fieldMetadata?.options?.map((opt: any, i: number) => ({
              key: opt.key ?? opt.value ?? opt.label ?? opt ?? i,
              value: opt.value ?? opt.label ?? opt,
            })) || [];
          switch (field.component) {
            case 'text':
              return (
                <React.Fragment key={this.getNameKey(field.name)}>
                  <ProspectLabelField index={index} formMode={formMode} />
                  <Input
                    className={field.styleClassNames}
                    id={field.name}
                    variant={
                      errors[field.name] &&
                      touched[field.name] &&
                      Input.Variant.Error
                    }
                    onBlur={handleBlur}
                    name={field.name}
                    label={
                      <span className="d-flex align-items-center w-100">
                        <TruncatedTextWithAutoTooltip
                          text={field?.displayName}
                          className="add-manually-prospect-field-label"
                        />
                        {isAiIconShow &&
                          fieldsData?.find(
                            (f) => f?.id?.toString() === field?.id?.toString(),
                          )?.isAiGenerated && (
                            <ImageIcon
                              src={Images.NoResultGradient}
                              alt="no-result-gradient"
                              className="bs-ml-6"
                              width={16}
                              height={16}
                            />
                          )}
                      </span>
                    }
                    value={values[field.name]}
                    onChange={(_val, e) => handleChange(e)}
                    type={field.component}
                    disabled={!!field.disabled}
                    caption={
                      <span className="form-input-error-caption red-txt-12">
                        {errors[field.name] &&
                          touched[field.name] &&
                          errors[field.name]}
                      </span>
                    }
                  />
                </React.Fragment>
              );

            case 'number':
              return (
                <React.Fragment key={this.getNameKey(field.name)}>
                  <ProspectLabelField index={index} formMode={formMode} />
                  <InputNumber
                    disabled={!!field.disabled}
                    containerClassName={field.styleClassNames}
                    id={field.name}
                    variant={
                      errors[field.name] &&
                      touched[field.name] &&
                      InputNumber.Variant.Error
                    }
                    onBlur={handleBlur}
                    name={field.name}
                    label={
                      <TruncatedTextWithAutoTooltip
                        text={field?.displayName}
                        className="bs-input-label-prefix"
                      />
                    }
                    value={values[field.name]}
                    onChange={(_val, e) => handleChange(e)}
                    caption={
                      <span className="form-input-error-caption red-txt-12">
                        {errors[field.name] &&
                          touched[field.name] &&
                          errors[field.name]}
                      </span>
                    }
                  />
                </React.Fragment>
              );

            case 'date':
              return (
                <React.Fragment key={this.getNameKey(field.name)}>
                  <ProspectLabelField index={index} formMode={formMode} />
                  <InputContainer
                    variant={Variant.Default}
                    label={field.displayName}
                    caption={null}
                    className={field.styleClassNames}
                  >
                    <DatePicker
                      selected={
                        values[field.name] ? new Date(values[field.name]) : null
                      }
                      onChange={(date: Date | null) => {
                        setFieldValue(
                          field.name,
                          date ? date.toLocaleDateString('en-CA') : '',
                        );
                      }}
                      onBlur={() =>
                        handleBlur({ target: { name: field.name } })
                      }
                      customInput={
                        <DatePickerInput
                          onClear={() => {
                            setFieldValue(field.name, '');
                            setFieldTouched(field.name, true);
                          }}
                        />
                      }
                      dateFormat="MMM d, yyyy"
                      placeholderText="Select date"
                    />
                    {errors[field.name] && touched[field.name] && (
                      <span className="form-input-error-caption red-txt-12">
                        {errors[field.name]}
                      </span>
                    )}
                  </InputContainer>
                </React.Fragment>
              );

            case 'currency': {
              const currencyData = fieldsData?.find(
                (item) => item?.fieldMetadata?.fieldId === field.id,
              )?.fieldMetadata?.metadata;
              return (
                <React.Fragment key={this.getNameKey(field.name)}>
                  <ProspectLabelField index={index} formMode={formMode} />
                  <InputNumber
                    containerClassName={`currency-input-wrapper ${field.styleClassNames}`}
                    id={field.name}
                    variant={
                      errors[field.name] &&
                      touched[field.name] &&
                      InputNumber.Variant.Error
                    }
                    onBlur={handleBlur}
                    name={field.name}
                    label={
                      <TruncatedTextWithAutoTooltip
                        text={field?.displayName}
                        className="d-flex"
                      />
                    }
                    value={values[field.name]}
                    onChange={(_val, e) => {
                      if (!_val) {
                        handleChange(e);
                        return;
                      }
                      if (/[^0-9]/.test(_val)) {
                        e.target.value = _val.replace(/[^0-9]/g, '');
                      }
                      // ✅ Enforce 10 character limit here
                      if (_val?.length > 15) {
                        e.target.value = _val?.slice(0, 15);
                      }
                      handleChange(e);
                    }}
                    showPrefix
                    currencyCode={currencyData?.currencyCode}
                  />
                  {errors[field.name] && touched[field.name] && (
                    <span className="form-input-error-caption red-txt-12">
                      {errors[field.name]}
                    </span>
                  )}
                </React.Fragment>
              );
            }

            case 'dropdown':
              return (
                <React.Fragment key={this.getNameKey(field.name)}>
                  <ProspectLabelField index={index} formMode={formMode} />
                  <div
                    className={`bs-input-container bs-select-container ${field.styleClassNames}`}
                  >
                    <label className="bs-input-label-prefix">
                      <TruncatedTextWithAutoTooltip
                        text={field?.displayName}
                        className="d-flex"
                      />
                    </label>
                    <Select
                      options={dropdownOptions || []}
                      selectedOptionKey={values[field.name]}
                      optionRenderer={(option) => <span>{option?.value}</span>}
                      selectedOptionRenderer={([option]) => (
                        <span>{option?.value}</span>
                      )}
                      onChange={([option]) =>
                        setFieldValue(field.name, option.key)
                      }
                      placement={Placement.Auto}
                      className={`${field.styleClassNames} custom-field-select`}
                      placeholder={`Select ${field.displayName} value`}
                      showSearch
                      filterOption={(value, option) =>
                        option?.value?.includes(value)
                      }
                      popperConfig={{
                        modifiers: [
                          {
                            name: 'flip',
                            options: {
                              fallbackPlacements: ['bottom'],
                              allowedAutoPlacements: ['top', 'bottom'],
                            },
                          },
                        ],
                      }}
                    />
                  </div>
                </React.Fragment>
              );
            default:
              break;
          }
        }
        return field.component instanceof React.Component && field.component;
      }

      if (field.component === 'long-text') {
        return (
          <div className="col-12" key={this.getNameKey(field.name)}>
            <div className="longtext-wrapper-form">
              <div className="longtext-label">
                <span className="longtext-header">
                  <InputLabel
                    text={field?.displayName}
                    place={InputLabel.Place.Before}
                  />
                  {isAiIconShow &&
                    fieldsData?.find(
                      (f) => f?.id?.toString() === field?.id?.toString(),
                    )?.isAiGenerated && (
                      <ImageIcon
                        src={Images.NoResultGradient}
                        alt="no-result-gradient"
                        className="bs-align-self-start"
                        width={16}
                        height={16}
                      />
                    )}
                </span>
              </div>
              <textarea
                name={field.name}
                value={values[field.name]}
                onBlur={handleBlur}
                className={`longtext-textarea ${
                  errors[field.name] && touched[field.name] ? 'error' : ''
                }`}
                onChange={(e) => setFieldValue(field.name, e.target.value)}
                rows={2}
              />
              {errors[field.name] && touched[field.name] && (
                <div className="form-input-error-caption red-txt-12">
                  {errors[field.name]}
                </div>
              )}
            </div>
          </div>
        );
      }
      return null;
    });

    return inputFields;
  }

  render() {
    const {
      fields,
      formBodyClasses,
      formContainerClasses,
      onSubmit,
      onCancel,
      buttonClassName,
      submitStatus,
      buttonTitle,
      primaryEmailChangeInProspectRequest,
      updatePrimaryNumberRequest,
      deleteEmailFromProspectRequest,
      deletePhoneFromProspectRequest,
      t,
      showCancelButton,
      selectedProspectId,
      prospectDetailAddField,
    } = this.props;
    const validationObject = this.generateValidationObject();
    if (!fields) {
      return null;
    }
    /* eslint-disable */
    const initialValues: any = fields
      .map((field: any) =>
        field.value
          ? { [field.name]: field.value }
          : field.name === 'phonenumber'
          ? { [field.name]: [] }
          : field.name === 'email'
          ? {
              [field.name]: [
                {
                  email: '',
                  emailType: ProspectContactInfoCategory.Work,
                  isPrimary: true,
                },
              ],
            }
          : { [field.name]: '' },
      )
      .reduce((acc, field) => ({ ...acc, ...field }), {});
    /* eslint-enable */

    return (
      <Formik
        innerRef={this.formRef}
        validate={(values) => {
          const errors = {};
          const validationObjectKeys = Object.keys(validationObject);
          validationObjectKeys.forEach((key) => {
            if (validationObject[key] === InputValidationType.isEmail) {
              values.email.forEach((v, _index) => {
                if (!validator[validationObject[key]](v.email)) {
                  errors[`email${_index}`] = 'Enter a valid email address';
                }
              });
            }
            if (validationObject[key] === InputValidationType.isMobilePhone) {
              values.phonenumber.forEach((v, _index) => {
                if (this.shouldSkipPhoneNumberValidation(v)) {
                  return;
                }
                if (!this.isValidPhoneNumber(v.phonenumber)) {
                  errors[`phonenumber${_index}`] = 'Enter a valid phone number';
                }
              });
            }
            if (validationObject[key] === InputValidationType.MaxCharLimit) {
              const fieldDef = fields.find((field) => field.name === key);
              const maxLimit =
                fieldDef && fieldDef.component === 'long-text' ? 10000 : 1000;
              if (!this.validateMaxCharLimit(values[key], maxLimit)) {
                errors[key] = `Characters limit exceeds ${maxLimit}`;
              }
            }
          });
          return errors;
        }}
        validateOnBlur
        initialValues={initialValues}
        onSubmit={onSubmit}
      >
        {({
          values,
          handleChange,
          errors,
          touched,
          dirty,
          handleBlur,
          setFieldValue,
          setFieldTouched,
          isSubmitting,
        }) => {
          // Check if form is dirty excluding primary email and phone number fields
          // Only when prospectDetailAddField prop is true
          const isFormDirty = prospectDetailAddField
            ? this.isFormDirtyExcludingContactFields(values, initialValues)
            : dirty;

          return (
            <Form
              className={`${formContainerClasses} d-flex h-100 justify-content-between flex-column prospect-form-field`}
            >
              <div className={`prospect-form-field-content ${formBodyClasses}`}>
                <div className="row">
                  {this.generateInputFields(
                    values,
                    handleChange,
                    errors,
                    touched,
                    handleBlur,
                    setFieldValue,
                    setFieldTouched,
                    primaryEmailChangeInProspectRequest,
                    updatePrimaryNumberRequest,
                    deleteEmailFromProspectRequest,
                    deletePhoneFromProspectRequest,
                    selectedProspectId,
                  )}
                </div>
              </div>
              <div className="d-flex justify-content-end prospect-modal-footer">
                {showCancelButton && (
                  <Button
                    className="pull-left mr-3 cancel-btn"
                    variant={Button.Variant.Default}
                    type={Button.Type.Button}
                    onClick={onCancel}
                  >
                    Cancel
                  </Button>
                )}
                <Button
                  className={`pull-right header-btn save-btn ${
                    buttonClassName || ''
                  }`}
                  variant={Button.Variant.Primary}
                  type={Button.Type.Submit}
                  isLoading={isSubmitting || submitStatus}
                  disabled={
                    !isEmpty(errors) ||
                    !isFormDirty ||
                    isSubmitting ||
                    submitStatus
                  }
                >
                  {buttonTitle || t('labels.save')}
                </Button>
              </div>
            </Form>
          );
        }}
      </Formik>
    );
  }
}

export default withTranslation()(CustomForm);
